import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Inscription, Responsive, useResponsive } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { FileImage } from '@/common/constants';
import ScanPreviewContainer from '@/components/controls/DropScan/ScanPreview/ScanPreviewContainer';
import useFilesInDocumentStore, {
  clearFilesInDocumentStore,
} from '@/components/controls/DropScan/useFilesInDocument.store';
import { useSnackbar } from '@/modules/core/hooks';
import useGetLetterOfMedicalNecessitiesFiles from '@/modules/MyDocuments/hooks/useGetLetterOfMedicalNecessitiesFiles';
import {
  NonClaimTransactionData,
  ProcessingStatusType,
  TransactionApprovalCode,
  TransactionData,
  TransactionType,
} from '@/modules/transaction/transaction.types';
import { getFileNameFromPath, safeDecodeURIComponent } from '@/utils/modifiers';

import { TransactionDocumentsPreviewModal } from './TransactionDocumentsPreview/TransactionDocumentsPreviewModal';
import { MissingOcrTransactionInfoModal } from './TransactionFileUploadPopup/MissingOcrTransactionInfoModal';
import {
  useQrTransactionFileUploadAdditionalActionsStore,
  useTransactionFileUploadPopupStore,
} from './TransactionFileUploadPopup/store';
import { SuccessOcrTransactionInfoModal } from './TransactionFileUploadPopup/SuccessOcrTransactionInfoModal';
import { TransactionFileUploadPopup } from './TransactionFileUploadPopup/TransactionFileUploadPopup';
import TransactionActivities from './TransactionNotification/TransactionActivities';
import TransactionProcessing from './TransactionProcessing/TransactionProcessing';
import TransactionDetailsLmnPopup from './TransactionSelectLmnFilePopup/TransactionDetailsLmnPopup';
import TransactionSelectLmnFilePopup from './TransactionSelectLmnFilePopup/TransactionSelectLmnFilePopup';
import EmployeeChoice from './EmployeeChoice';
import TransactionCard from './TransactionCard';
import TransactionPaymentDetails from './TransactionPaymentDetails';
import TransactionStatuses from './TransactionStatuses';
import useFilesByDocumentIdQuery from './useFilesByDocumentId.query';
import useUpdateClaimDocumentIdQuery from './useUpdateClaimDocumentId.query';

interface NonClaimProps {
  data: NonClaimTransactionData;
  postingId: string;
  claimId?: undefined;
  employeeId?: string;
  hasAddDoc?: boolean;
}

interface ClaimProps {
  data: TransactionData;
  claimId: string;
  postingId?: undefined;
  employeeId?: string;
  hasAddDoc?: boolean;
}

type Props = NonClaimProps | ClaimProps;

const TransactionDetails: React.FC<Props> = (props) => {
  const [isClaimDocumentIdUpdated, setIsClaimDocumentIdUpdated] = useState(false);

  const [isFileUploadPopupVisible, setIsFileUploadPopupVisible] = useState(false);
  const [isLMNUploadPopupVisible, setIsLMNUploadPopupVisible] = useState(false);
  const [isTransactionLmnPopupVisible, setIsTransactionLmnPopupVisible] = useState(false);
  const [isScannedFileApprovedModal, setIsScannedFileApprovedModal] = useState(false);
  const [isScannedFileNotApprovedModal, setIsScannedFileNotApprovedModal] = useState(false);

  const { handleSetValues, handleReset, values } = useFilesInDocumentStore();
  const documentId = useMemo(() => (props.claimId ? props.data?.documentId : ''), [props]);
  const { files: claimFiles } = useFilesByDocumentIdQuery(documentId || values.documentId);

  const letterOfMedicalNecessities = useMemo(() => {
    if (props?.claimId) {
      return props?.data?.letterOfMedicalNecessities || [];
    }
    return [];
  }, [props]);

  const { letterOfMedicalNecessitiesFiles } = useGetLetterOfMedicalNecessitiesFiles({
    letterOfMedicalNecessities,
  });

  const isAiApproved = useTransactionFileUploadPopupStore((store) => store.aiApproved);
  const isDocumentHasBeenScanned = useTransactionFileUploadPopupStore(
    (store) => store.isDocumentHasBeenScanned
  );
  const isCancelScanning = useTransactionFileUploadPopupStore((store) => store.isCancelScanning);
  const setIsScanned = useTransactionFileUploadPopupStore((store) => store.handleSetIsDocumentHasBeenScanned);
  const handleResetOcrDocument = useTransactionFileUploadPopupStore((store) => store.handleReset);
  const handleSetOcrClaimId = useQrTransactionFileUploadAdditionalActionsStore(
    (store) => store.handleSetClaimId
  );

  const [activeFile, setActiveFile] = useState(0);
  const [isDocPreviewModalVisible, setIsDocPreviewModalVisible] = useState(false);
  const [allFiles, setAllFiles] = useState<FileImage[]>([]);
  const [hasFileForPreview, setHasFileForPreview] = useState(false);
  const { t } = useTranslation();
  const addDocButtonRef = useRef(null);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const showInfoMessage = useCallback(() => {
    handleAddPermanentSnackbar({
      text: t('Document added. The claim is about to be processed.'),
      closeIcon: true,
      // state: SnackbarState.positive,
    });
  }, [handleAddPermanentSnackbar, t]);
  const { updateClaimDocumentId, isSuccess } = useUpdateClaimDocumentIdQuery(props.claimId);

  const hasScanPreview = useMemo(
    () => (props.hasAddDoc || isSuccess || values.files?.length > 0) && allFiles?.length > 0,
    [props.hasAddDoc, isSuccess, values.files, allFiles]
  );

  useEffect(() => {
    if (isDocumentHasBeenScanned && !isCancelScanning) {
      if (isAiApproved) {
        setIsScannedFileApprovedModal(true);
      } else {
        setIsScannedFileNotApprovedModal(true);
      }
    }
  }, [
    isAiApproved,
    isCancelScanning,
    isDocumentHasBeenScanned,
    setIsScannedFileApprovedModal,
    setIsScannedFileNotApprovedModal,
  ]);

  useEffect(() => {
    if (values.files?.length) {
      setAllFiles(values.files);
    }
  }, [values.files]);

  useEffect(() => {
    if (documentId && !values.documentId) handleSetValues({ documentId: toString(documentId) });
  }, [documentId, handleSetValues, values.documentId]);

  useEffect(() => {
    const allFiles = [...(claimFiles || []), ...(letterOfMedicalNecessitiesFiles || [])];

    if (allFiles.length) {
      handleSetValues({ files: allFiles });
    }
  }, [claimFiles, letterOfMedicalNecessitiesFiles, handleSetValues]);

  useEffect(
    () => () => {
      handleReset();
      handleResetOcrDocument();
      clearFilesInDocumentStore();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (props.claimId) {
      handleSetOcrClaimId(props.claimId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimId]);

  const currentFileName = useMemo(
    () => getFileNameFromPath(allFiles?.[activeFile]?.name || allFiles?.[activeFile]?.path),
    [activeFile, allFiles]
  );
  const decodedFileName = useMemo(() => safeDecodeURIComponent(currentFileName || ''), [currentFileName]);
  const hasTransactionProcessingSection = useMemo(
    () =>
      props?.claimId &&
      props?.data &&
      props.data.processingStatus === ProcessingStatusType.ActionRequired &&
      props.data?.processingSubstatusType === TransactionApprovalCode.CARRIER_CLAIM,
    [props]
  );
  const hasEmployeeChoiceSection = useMemo(
    () =>
      props.claimId &&
      props.data &&
      props.employeeId &&
      props.data.processingSubstatusType === TransactionApprovalCode.WAITING_FOR_FUNDS &&
      props.data.processingStatus === ProcessingStatusType.ActionRequired,
    [props]
  );

  const renderScanPreviewContainer = useCallback(
    ({
      isPreviewHidden,
      disableUpload,
      isRemovingDisabled,
    }: {
      isPreviewHidden: boolean;
      disableUpload?: boolean;
      isRemovingDisabled?: boolean;
    }) => (
      <ScanPreviewContainer
        isPreviewHidden={isPreviewHidden}
        disableUpload={disableUpload}
        isRemovingDisabled={isRemovingDisabled}
        isSmallViewVersion
        isThumbsTopPosition
        isFlex
        defaultFiles={allFiles}
        addFileButtonRef={addDocButtonRef}
        onSetPreviewMode={() => setIsDocPreviewModalVisible(true)}
        onSetActivePage={setActiveFile}
      />
    ),
    [allFiles]
  );

  const postingPaymentFeeData = useMemo(() => {
    if (
      (props.data?.transactionType === TransactionType.ACCOUNT_MAINTENANCE_EE_FEE ||
        props.data?.transactionType === TransactionType.PAPER_STATEMENT_FEE ||
        props.data?.transactionType === TransactionType.PAPER_STATEMENT_EE_FEE) &&
      props?.data?.paymentFees?.length
    ) {
      return props.data as TransactionData;
    }
  }, [props.data]);
  const { isMobile } = useResponsive();

  return (
    <Box direction="column" data-testid="TransactionDetails-wrapper">
      <Box pad="spacing24" background="module" round="moduleRound">
        <Box>
          <Box direction={isMobile ? 'column' : 'row'}>
            <Responsive size={Responsive.Mobile}>
              <Box margin={{ bottom: 'spacing12' }}>
                <TransactionStatuses isMobileView statuses={props.data?.statuses || []} />
              </Box>
            </Responsive>
            <Box width="100%" margin={{ right: 'spacing24' }}>
              <TransactionCard
                className="tranaction-card"
                {...(props.claimId
                  ? {
                      invoiceId: props.data?.invoiceId,
                      id: props.data?.id,
                      approvedAmount: props.data?.approvedAmount,
                      paidAmount: props.data?.paidAmount,
                      userName: props.data?.serviceFor.name,
                      userRelationship: props.data?.serviceFor?.relationshipType,
                      birthOn: props?.data?.serviceFor?.birthOn,
                      serviceFor: props.data?.serviceFor,
                      service: props.data?.service,
                      transactionType: props.data?.paymentType,
                      transactorId: props.data?.providerId,
                      applyDeductibleAmount: props.data?.applyDeductibleAmount,
                      date: props.data?.serviceOn,
                      employeeNotes: props.data?.employeeNotes,
                    }
                  : {})}
                {...(props.postingId
                  ? {
                      userName: props.data?.planName,
                      date: props.data?.statusDate,
                      id: props.data?.postingId,
                    }
                  : {})}
                category={props.data?.category}
                providerName={props.data?.memo}
                amount={props.data?.amount}
                claimId={props.claimId}
                transactionType={
                  props.data?.transactionType || props.data?.paymentType || TransactionType.CHECK_CLEARED
                }
                taxYear={props.data?.taxYear}
              />
              {hasTransactionProcessingSection ? (
                <TransactionProcessing
                  claimId={props?.claimId}
                  paymentType={props?.data?.paymentType}
                  processingStatus={props?.data?.processingStatus}
                  providerId={`${props?.data?.providerId || ''}`}
                />
              ) : null}
              {hasEmployeeChoiceSection ? (
                <EmployeeChoice employeeId={props.employeeId} claimId={props.claimId} />
              ) : null}
            </Box>
            <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
              <TransactionStatuses statuses={props.data?.statuses || []} />
            </Responsive>
          </Box>
          <TransactionPaymentDetails
            service={props.data?.service}
            employeeId={props.employeeId}
            claimId={props.claimId}
            postingId={props.postingId}
            paymentMethodId={props?.data?.paymentMethodId}
            transactionType={props.data?.transactionType}
            {...(props.claimId ? { data: props.data } : {})}
          />
        </Box>
      </Box>
      {props.claimId ? (
        <TransactionActivities
          data={props.data}
          onUploadDocument={() => setIsFileUploadPopupVisible(true)}
          onUploadLmnDocument={() => setIsLMNUploadPopupVisible(true)}
        />
      ) : null}
      {postingPaymentFeeData ? <TransactionActivities data={postingPaymentFeeData} /> : null}

      {hasScanPreview && (
        <Box margin={{ top: 'spacing24' }} pad="spacing24" background="module" round="moduleRound">
          <Inscription
            margin={{ bottom: 'spacing24' }}
            size="large"
            lineHeight="22px"
            weight="bold"
            color="textTitle"
          >
            {t('Documents')}
          </Inscription>
          <Box>
            {renderScanPreviewContainer({
              isPreviewHidden: false,
              disableUpload: true,
              isRemovingDisabled: true,
            })}
          </Box>
          {/* {renderScanPreviewContainer({
            isPreviewHidden: false,
            disableUpload: true,
            isRemovingDisabled: true,
          })} */}
        </Box>
      )}
      <TransactionDocumentsPreviewModal
        visible={isDocPreviewModalVisible}
        onSetVisible={setIsDocPreviewModalVisible}
        modalTitle={decodedFileName}
      >
        <ScanPreviewContainer
          defaultFiles={allFiles}
          isShowTooltip={false}
          addFileButtonRef={addDocButtonRef}
          onSetActivePage={setActiveFile}
          isActiveFileShown
          disableUpload
          isRemovingDisabled
          activeFile={activeFile}
          onSetFiles={(files) => {
            setAllFiles(files as FileImage[]);
            if (hasFileForPreview !== files.length > 0) {
              setHasFileForPreview(!hasFileForPreview);
            }
          }}
        />
      </TransactionDocumentsPreviewModal>

      {isFileUploadPopupVisible ? (
        <TransactionFileUploadPopup
          claimId={props?.claimId}
          visible={isFileUploadPopupVisible}
          handleChangeVisibility={setIsFileUploadPopupVisible}
        />
      ) : null}

      {isLMNUploadPopupVisible && props?.claimId ? (
        <TransactionSelectLmnFilePopup
          onSetVisible={setIsLMNUploadPopupVisible}
          setIsTransactionLmnPopupVisible={setIsTransactionLmnPopupVisible}
          claimId={props?.claimId}
          employeeId={props?.employeeId}
          dateOfService={props?.data?.serviceOn}
        />
      ) : null}

      {isTransactionLmnPopupVisible ? (
        <TransactionDetailsLmnPopup
          createForClaimId={props?.claimId}
          onSetVisible={setIsTransactionLmnPopupVisible}
        />
      ) : null}

      <MissingOcrTransactionInfoModal
        visible={isScannedFileNotApprovedModal && !isFileUploadPopupVisible}
        onSetVisible={(value: boolean) => {
          setIsScannedFileNotApprovedModal(value);
          if (isClaimDocumentIdUpdated && isSuccess) {
            showInfoMessage();
          }
        }}
        onCancel={() => {
          // setIsScannedFileNotApprovedModal(false);
          setIsScanned(false);
          updateClaimDocumentId(values.documentId);
          setIsClaimDocumentIdUpdated(true);
        }}
        onSubmit={() => {
          setIsScannedFileNotApprovedModal(false);
          setIsScanned(false);
          setIsFileUploadPopupVisible(true);
        }}
      />
      <SuccessOcrTransactionInfoModal
        visible={isScannedFileApprovedModal}
        onSetVisible={() => setIsScannedFileApprovedModal(false)}
        onSubmit={() => setIsScannedFileApprovedModal(false)}
      />
    </Box>
  );
};

export default TransactionDetails;
